/*
 * Copyright (c) 2008-present Sonatype, Inc.
 *
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/pro/attributions
 * Sonatype and Sonatype Nexus are trademarks of Sonatype, Inc. Apache Maven is a trademark of the Apache Foundation.
 * M2Eclipse is a trademark of the Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import React from 'react';
import {useMachine, useService} from '@xstate/react';

import SamlConfigurationMachine from './SamlConfigurationMachine';
import {
  ExtJS,
  Alert,
  Button,
  ContentBody,
  FieldWrapper,
  NxLoadWrapper,
  NxSubmitMask,
  Select,
  Section,
  SectionFooter,
  Textarea,
  Textfield
} from 'nexus-ui-plugin';
import UIStrings from "../../../../constants/UIStrings";

import './SamlConfiguration.scss';

const FIELDS = UIStrings.SAML_CONFIGURATION.FIELDS;

export default function SamlConfiguration() {
  const service = useMachine(SamlConfigurationMachine, {devTools: true})[2];

  return <SamlConfigurationForm service={service}/>;
}

/**
 * @param service - an xstate machine which provides the business logic for this form
 * @return the SamlConfigurationForm component
 */
export function SamlConfigurationForm({service}) {
  const [current, send] = useService(service);

  const context = current.context;
  const isLoading = current.matches('loading');
  const isSaving = current.matches('saving');
  const settings = context.data;
  const isPristine = context.isPristine;
  const isInvalid = context.isInvalid;
  const isEntityIdUri = context.isEntityIdUri;

  function updateSettings({target}) {
    send('UPDATE', {
      data: {
        ...settings,
        [target.name]: target.value
      }
    });
  }

  function saveSettings(event) {
    event.preventDefault();
    send('SAVE');
  }

  function discardChanges() {
    send('DISCARD');
  }

  function handleEnter(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      send('SAVE');
    }
  }

  let error = null;
  if (context.error instanceof Array) {
    error = <Alert type="error">
      {UIStrings.SAML_CONFIGURATION.MESSAGES.SAVE_ERROR}
      <ul>
        {context.error.map(error => <li key={error.id}>{JSON.stringify(error)}</li>)}
      </ul>
    </Alert>;
  }
  else if (context.error) {
    error = <Alert type="error">
      {UIStrings.SAML_CONFIGURATION.MESSAGES.SAVE_ERROR}<br/>
      {context.error}
    </Alert>;
  }

  ExtJS.setDirtyStatus('SamlConfiguration', !isPristine);

  return <ContentBody className='nxrm-saml-configuration'>
    <Section>
      <NxLoadWrapper loading={isLoading}>
        {isSaving && <NxSubmitMask message={UIStrings.SAVING}/>}
        {error}

        <FieldWrapper labelText={FIELDS.idpMetadataLabel}>
          <Textarea isRequired name="idpMetadata" value={settings.idpMetadata} onChange={updateSettings}/>
        </FieldWrapper>

        <FieldWrapper labelText={FIELDS.entityIdUriLabel} descriptionText={FIELDS.entityIdUriDescription}>
          <Textfield
              isRequired
              name="entityIdUri"
              value={settings.entityIdUri}
              onChange={updateSettings}
              onKeyPress={handleEnter}
              isValid={isEntityIdUri}
              validityMessage={UIStrings.SAML_CONFIGURATION.FIELDS.entityIdUriValidationError}
          />
        </FieldWrapper>

        <FieldWrapper labelText={FIELDS.validateResponseSignatureLabel} descriptionText={FIELDS.validateResponseSignatureDescription}>
          <Select isRequired name="validateResponseSignature" value={settings.validateResponseSignature} onChange={updateSettings}>
            <option value="default">Default</option>
            <option value="true">True</option>
            <option value="false">False</option>
          </Select>
        </FieldWrapper>

        <FieldWrapper labelText={FIELDS.validateAssertionSignatureLabel} descriptionText={FIELDS.validateAssertionSignatureDescription}>
          <Select isRequired name="validateAssertionSignature" value={settings.validateAssertionSignature} onChange={updateSettings}>
            <option value="default">Default</option>
            <option value="true">True</option>
            <option value="false">False</option>
          </Select>
        </FieldWrapper>

        <fieldset>
          <legend>IdP Field Mappings</legend>

          <FieldWrapper labelText={FIELDS.usernameAttrLabel}>
            <Textfield
                isRequired
                name="usernameAttr"
                value={settings.usernameAttr}
                onChange={updateSettings}
                onKeyPress={handleEnter}
            />
          </FieldWrapper>

          <FieldWrapper labelText={FIELDS.firstNameAttrLabel}>
            <Textfield
                name="firstNameAttr"
                value={settings.firstNameAttr}
                onChange={updateSettings}
                onKeyPress={handleEnter}
            />
          </FieldWrapper>

          <FieldWrapper labelText={FIELDS.lastNameAttrLabel}>
            <Textfield
                name="lastNameAttr"
                value={settings.lastNameAttr}
                onChange={updateSettings}
                onKeyPress={handleEnter}
            />
          </FieldWrapper>

          <FieldWrapper labelText={FIELDS.emailAttrLabel}>
            <Textfield
                name="emailAttr"
                value={settings.emailAttr}
                onChange={updateSettings}
                onKeyPress={handleEnter}
            />
          </FieldWrapper>

          <FieldWrapper fieldName="roleAttr" labelText={FIELDS.roleAttrLabel}>
            <Textfield
                name="roleAttr"
                value={settings.roleAttr}
                onChange={updateSettings}
                onKeyPress={handleEnter}
            />
          </FieldWrapper>
        </fieldset>

        <SectionFooter>
          <Button variant="primary" disabled={isPristine || isInvalid} onClick={saveSettings} type="submit">
            {UIStrings.SETTINGS.SAVE_BUTTON_LABEL}
          </Button>
          <Button disabled={isPristine} onClick={discardChanges}>
            {UIStrings.SETTINGS.DISCARD_BUTTON_LABEL}
          </Button>
        </SectionFooter>
      </NxLoadWrapper>
    </Section>
  </ContentBody>;
}
